import { getAnalytics } from "@tm/insurance/analytics";
import { navigationActions } from "@tm/insurance/navigation";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { TextNodeType } from "@tm/ui-widgets/components";
import { theme } from "@tm/utility/global";
import React from "react";
import FlexComp from "../FlexComp/FlexComp";
import FormattedText from "../FormattedText/FormattedText";
import { ImageComponent } from "../ImageComponent/ImageComponent";
import {
  resolveNetPriceField,
  useNetPriceContext,
} from "../mypartner/contexts/NetPriceContext";
import AnchorTag from "../TextStyles/AnchorTag";
import {
  BookingDetailsRow,
  CancellationCompactRight,
  CancellationSection,
  CancellationSectionPwa,
  PlanHeader,
  PlanPriceContainer,
  Separator,
} from "./PlanPriceInfoComp.styles";

type PlanInfo = {
  name: {
    text: {
      value: string;
      styles?: any;
    };
  };
  icon: string;
};

type CancellationInfo = {
  title: TextNodeType;
  info: {
    prefix: TextNodeType;
    date: number;
  };
};

export type PlanPriceInfoData = {
  planType: TextNodeType;
  planInfo: PlanInfo;
  sumInsured: TextNodeType;
  amountToPay: TextNodeType;
  netAmountToPay?: TextNodeType;
  cancellationInfo?: CancellationInfo;
};

export type PlanPriceInfoCompProps = PlanPriceInfoData & {
  onEditClick?: () => void;
};

const formatDate = (timestamp: number | undefined): string => {
  if (!timestamp) return "";
  const date = new Date(timestamp * 1000);
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes.toString().padStart(2, "0");

  return `${day} ${month}, ${year}, ${formattedHours}:${formattedMinutes}${ampm} IST`;
};

const EDIT_ICON =
  "https://tripmoneycmsimgak.mmtcdn.com/img/edit_Icon_be6c24806a.svg";

/**
 * Splits a TextNodeType array into [first segment, remaining segments] and
 * renders them in a space-between row (label left, value(s) right).
 */
const SplitDetailRow = ({
  value,
  baseStyle,
}: {
  value?: TextNodeType;
  baseStyle: React.CSSProperties;
}) => {
  const txt = (value as TextNodeType)?.text;
  if (!Array.isArray(txt) || txt.length < 2) {
    return (
      <BookingDetailsRow>
        <FormattedText value={value} baseStyle={baseStyle} />
      </BookingDetailsRow>
    );
  }
  const label = { text: txt[0] } as TextNodeType;
  const vals = { text: txt.slice(1) } as TextNodeType;
  return (
    <BookingDetailsRow>
      <span style={{ flex: 1, minWidth: 0 }}>
        <FormattedText value={label} baseStyle={baseStyle} />
      </span>
      <span style={{ flexShrink: 0 }}>
        <FormattedText
          value={vals}
          baseStyle={{ ...baseStyle, fontWeight: 900 }}
        />
      </span>
    </BookingDetailsRow>
  );
};

const PlanPriceInfoComp = ({
  planType,
  planInfo,
  sumInsured,
  amountToPay,
  netAmountToPay,
  cancellationInfo,
}: PlanPriceInfoCompProps) => {
  const analytics = getAnalytics();
  const device = useBreakpointV2();
  const { showNetPrice } = useNetPriceContext();
  const activeAmountToPay = resolveNetPriceField(
    amountToPay,
    netAmountToPay,
    showNetPrice
  );

  const handleEditClick = () => {
    analytics?.trackTravDetailsEvents({
      actionType: "edit_plan_price_info_clicked",
    });
    if (document.referrer) {
      navigationActions.goBack();
    }
    const queryParams = new URLSearchParams(window.location.search);
    const partnerBookingId = queryParams.get("partnerBookingId");
    const params = {
      countries: queryParams.get("countries"),
      startDate: queryParams.get("startDate"),
      endDate: queryParams.get("endDate"),
      travellers: queryParams.get("travellers"),
      segment: queryParams.get("segment"),
      pricingView: queryParams.get("pricingView"),
      type: queryParams.get("type") || "",
      itineraryId: queryParams.get("itineraryId") || "",
      ...(partnerBookingId && {
        partnerBookingId,
      }),
    };
    navigationActions.navigateToListing(params);
  };

  const formattedDate = formatDate(cancellationInfo?.info?.date);
  const prefixParts = Array.isArray(cancellationInfo?.info?.prefix?.text)
    ? cancellationInfo?.info?.prefix?.text
    : cancellationInfo?.info?.prefix?.text
    ? [cancellationInfo?.info?.prefix?.text]
    : [];
  const cancellationPrefix = prefixParts
    .map((segment: any) =>
      typeof segment === "string" ? segment : segment?.value || ""
    )
    .join(" ")
    .trim();
  const isFreeCancellation = /free\s*cancellation/i.test(cancellationPrefix);
  const shouldUsePwaFreeCancellationLayout =
    device !== "lg" && isFreeCancellation;
  const CancellationWrapper = shouldUsePwaFreeCancellationLayout
    ? CancellationSectionPwa
    : CancellationSection;

  return (
    <PlanPriceContainer>
      <PlanHeader>
        <FlexComp justifyContent="space-between" alignItems="center">
          <FormattedText
            value={planType}
            baseStyle={{
              fontWeight: 900,
              fontSize: "18px",
              color: theme.blackText,
            }}
          />
          {device === "lg" && (
            <AnchorTag handleClick={handleEditClick}>
              <ImageComponent width="16px" height="16px" src={EDIT_ICON} />
            </AnchorTag>
          )}
        </FlexComp>
      </PlanHeader>

      <Separator />

      <FlexComp gap="16px" alignItems="center" margin="0 0 12px 0">
        {planInfo.icon && (
          <ImageComponent width="50px" height="29px" src={planInfo.icon} />
        )}
        <FormattedText
          value={planInfo.name}
          baseStyle={{
            fontWeight: 900,
            fontSize: "16px",
            color: theme.blackText,
          }}
        />
      </FlexComp>

      <FlexComp flexDirection="column" gap="12px" margin="0 0 12px 0">
        <SplitDetailRow
          value={sumInsured}
          baseStyle={{
            fontWeight: 400,
            fontSize: "14px",
            color: theme.greyText,
          }}
        />
        <SplitDetailRow
          value={activeAmountToPay}
          baseStyle={{
            fontWeight: 400,
            fontSize: "14px",
            color: theme.greyText,
          }}
        />
      </FlexComp>

      {cancellationInfo ? (
        <CancellationWrapper>
          {shouldUsePwaFreeCancellationLayout ? (
            <>
              <FormattedText
                value={cancellationInfo.title}
                baseStyle={{
                  fontWeight: 700,
                  fontSize: "14px",
                  color: theme.greyText,
                }}
              />
              <CancellationCompactRight>
                <FormattedText
                  value={cancellationInfo.info?.prefix}
                  baseStyle={{
                    fontSize: "14px",
                    fontWeight: 400,
                    color: theme.greyText,
                  }}
                />
                {formattedDate ? (
                  <FormattedText
                    value={formattedDate}
                    baseStyle={{
                      fontSize: "12px",
                      color: theme.greenText5,
                      fontWeight: 900,
                      textAlign: "right",
                    }}
                  />
                ) : null}
              </CancellationCompactRight>
            </>
          ) : (
            <>
              <FormattedText
                value={cancellationInfo.title}
                baseStyle={{
                  fontWeight: 700,
                  fontSize: "14px",
                  color: theme.greyText,
                }}
              />
              <FlexComp gap="4px" alignItems="center">
                <FormattedText
                  value={cancellationInfo.info?.prefix}
                  baseStyle={{
                    fontSize: "14px",
                    color: theme.greyText,
                  }}
                />
                {formattedDate ? (
                  <FormattedText
                    value={formattedDate}
                    baseStyle={{
                      fontSize: "14px",
                      color: theme.greyText,
                      fontWeight: 700,
                    }}
                  />
                ) : null}
              </FlexComp>
            </>
          )}
        </CancellationWrapper>
      ) : null}
    </PlanPriceContainer>
  );
};

export default PlanPriceInfoComp;
